import { useEffect, useRef, useState } from "react";
import api, { mediaSrc } from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, ImageOff, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function MediaLibrary() {
  const [media, setMedia] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delAck, setDelAck] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/media").then((r) => setMedia(r.data)).catch(() => setMedia([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/media/upload", fd); toast.success("התמונה הועלתה"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "העלאה נכשלה"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doDelete = async () => {
    try { await api.delete(`/media/${delTarget.id}?confirm=true`); toast.success("התמונה נמחקה"); setDelTarget(null); load(); }
    catch { toast.error("המחיקה נכשלה"); }
  };

  return (
    <EditorLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">ספריית מדיה</h1>
          <p className="text-[#5C635C] mt-1">כל התמונות שלך במקום אחד. אפשר להשתמש בהן בכל בלוק.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="media-file-input" />
        <Button data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-2">
          <Upload className="w-4 h-4" /> {uploading ? "מעלה..." : "העלאת תמונה"}
        </Button>
      </div>

      {media === null ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div>
      ) : media.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E2D9]">
          <ImageOff className="w-10 h-10 mx-auto text-[#c8c4b8] mb-3" />
          <p className="text-[#5C635C]">עדיין אין תמונות. העלי את הראשונה!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="group relative aspect-square rounded-xl overflow-hidden border border-[#E5E2D9] bg-white">
              <img src={mediaSrc(m.url)} alt={m.original_filename} className="w-full h-full object-cover" />
              <button onClick={() => { setDelAck(false); setDelTarget(m); }} data-testid={`media-delete-${m.id}`}
                      className="absolute top-2 left-2 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white">
                <Trash2 className="w-4 h-4 text-[#8C3A3A]" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent dir="rtl" className="font-editor" data-testid="media-delete-dialog">
          <DialogHeader><DialogTitle className="text-right flex items-center gap-2 text-[#8C3A3A]"><AlertTriangle className="w-5 h-5" /> מחיקת תמונה</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#5C635C]">
            <p className="bg-red-50 border border-red-100 rounded-lg p-3 text-[#8C3A3A]">אם התמונה בשימוש באחד הבלוקים — היא תיעלם משם באתר. ודאי שאינה בשימוש לפני מחיקה.</p>
            <label className="flex items-start gap-2">
              <input type="checkbox" data-testid="media-delete-ack" checked={delAck} onChange={(e) => setDelAck(e.target.checked)} className="accent-[#8C3A3A] w-4 h-4 mt-0.5" />
              אני מבינה שהתמונה תוסר לצמיתות.
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} className="rounded-full">ביטול</Button>
            <Button data-testid="media-delete-confirm" onClick={doDelete} disabled={!delAck} className="bg-[#8C3A3A] hover:bg-[#742f2f] rounded-full disabled:opacity-40">מחיקה</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EditorLayout>
  );
}
